/**
 * ===========================================================================
 *  FRONT END — warrior select, battlefield select, muster
 * ===========================================================================
 *
 * The three screens between the title card and the first blow, in the shape
 * Dynasty Warriors uses: pick the officer, pick the field, read the orders,
 * go. `menus.js` owns the modal state machine and hands these screens their
 * turn; this file owns what is inside them.
 *
 * ---------------------------------------------------------------------------
 * THE WARRIOR ON SCREEN IS THE REAL ONE
 * ---------------------------------------------------------------------------
 * A character-select screen that shows a picture is a menu. This one puts the
 * camera on the LIVE hero — the same rigged, skinned man the battle uses,
 * standing in the valley under the real sun — and turns him slowly on the
 * boom (`camrig.setMode('select')` + a persistent `camrig.orbit()`). Moving
 * the highlight calls `roster.previewHero()`, which swaps both the moveset and
 * the steel in his fist, so the glaive becomes a pair of sabres in front of
 * you before you have confirmed anything. The centre third of the frame is
 * left empty for him; the rail and the dossier live in the outer thirds.
 *
 * ---------------------------------------------------------------------------
 * THE MAP IS THE REAL VALLEY
 * ---------------------------------------------------------------------------
 * The battlefield plate is not an illustration either: it is the minimap's own
 * baked relief of the shipped heightfield (`minimap.relief`), with the river,
 * the roads and the seven holdings inked over it straight from
 * `src/world/layout.js`. The stage the player highlights lights its own
 * holdings and drops a landing chevron where the hero will actually stand.
 *
 * ---------------------------------------------------------------------------
 * NO CSS ANIMATION, NO WEBFONTS, NO ASSETS
 * ---------------------------------------------------------------------------
 * Same three house rules as the rest of the HUD (see style.js): Playwright
 * screenshots with `animations:'disabled'`, the capture harness aborts every
 * off-origin request, and every dimension is `calc(var(--u) * n)` so the
 * composition is identical from 1280x720 to 4K. Everything here is DOM, CSS
 * and 2D canvas generated in code.
 *
 * ---------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------
 *   createSelect(host, deps) -> {
 *     el,                 the container (three .modal children)
 *     enter(state),       called by menus.js when one of the screens opens
 *     leave(state),       called when it closes
 *     key(ev, state),     returns true if the key was consumed
 *     resize(),           re-fit every canvas
 *     choice,             { heroId, arenaId }
 *     hero(), arena(),    the chosen records
 *   }
 *   deps: { world, sound, onConfirm(state), onBack(state) }
 */
import {
  HEROES, STAGES, ARENAS, ACTIVE_ARENA, STAT_KEYS, STAT_LABEL, FOE_COMMAND,
  statsFor, signatureFor, previewHero,
} from '../game/roster.js';
import { RIVER_PTS, ROAD_DEFS, SITES, BRIDGE, HAS_BRIDGE, ARENA_ID } from '../world/layout.js';
import { WALLS } from '../world/walls.js';
import { fit, portrait } from './gfx.js';
import { uiColor, rgbOf, factionOf, SIDE } from '../core/theme.js';

/* The two hosts, named once in theme.js — the legend and the orders both
 * name them, and neither should carry its own copy. */
const ALLIES = factionOf(SIDE.PLAYER), FOES = factionOf(SIDE.ENEMY);

const STATES = ['warriors', 'arena', 'briefing'];
const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * Difficulty pips as MARKUP, never as a glyph.
 *
 * A <div>, deliberately, not a <span>: the roster card styles its own
 * `.cn span` as a block, that selector outweighs `.sb .pips`, and the five
 * diamonds came out stacked in a vertical column on the first capture. The sheet ships no webfont (the
 * capture harness aborts off-origin requests), and a star character is not
 * guaranteed to exist in Liberation Sans or DejaVu — a missing one renders as
 * a tofu box on somebody's machine and nowhere in testing. These are rotated
 * divs, which exist everywhere.
 */
const pips = (n, cls = '') => `<div class="pips ${cls}">` +
  [1, 2, 3, 4, 5].map(k => `<u class="${k <= n ? 'f' : ''}"></u>`).join('') + '</div>';

/* ============================================================ the stylesheet */

export const SELECT_CSS = `
/* ========================================================= SELECT SCREENS */
.sb .sel{align-items:stretch; justify-content:flex-start}
/* Side scrims only. A full-frame wash would bury the hero the screen exists to
   show; these darken the two outer thirds the panels sit in and leave the
   centre reading as the live world it is. */
.sb .sel .selscrim{position:absolute; inset:0;
  background:
    linear-gradient(90deg, rgba(4,3,3,.97) 0%, rgba(4,3,3,.93) 19%, rgba(4,3,3,0) 36%),
    linear-gradient(270deg, rgba(4,3,3,.97) 0%, rgba(4,3,3,.94) 27%, rgba(4,3,3,0) 43%),
    linear-gradient(180deg, rgba(4,3,3,.90), rgba(4,3,3,.10) 24%, rgba(4,3,3,.10) 60%, rgba(4,3,3,.92));
}
/* Warrior select keeps its centre clear because the hero is the subject.
   Battlefield select's subject is the MAP, so the world behind it is only
   atmosphere and gets a full wash — a half-lit valley competing with an inked
   plate of the same valley reads as two maps fighting. */
.sb .sel.stagesel .selscrim{background:
    radial-gradient(ellipse 88% 82% at 50% 48%, rgba(6,5,4,.94), rgba(2,2,2,.975))}
.sb .sel.brief .selscrim{background:
    radial-gradient(ellipse 92% 84% at 50% 46%, rgba(6,5,4,.93), rgba(2,2,2,.985))}

.sb .selhead{position:absolute; left:0; right:0; top:calc(var(--u)*26); text-align:center; z-index:2}
.sb .steps{display:flex; gap:calc(var(--u)*10); justify-content:center; align-items:center;
  font-size:calc(var(--u)*9); font-weight:700; letter-spacing:calc(var(--u)*3.4); text-transform:uppercase}
.sb .steps span{color:rgba(214,192,150,.30)}
.sb .steps i{font-style:normal; color:rgba(214,192,150,.34)}
.sb .steps b{color:#ffe6ae; text-shadow:0 0 calc(var(--u)*18) rgba(255,180,70,.55)}
.sb .seltitle{margin-top:calc(var(--u)*8); font-size:calc(var(--u)*30); letter-spacing:calc(var(--u)*11);
  color:#fbf0d6; line-height:1;
  text-shadow:0 calc(var(--u)*3) calc(var(--u)*20) rgba(0,0,0,.92), 0 0 calc(var(--u)*44) rgba(228,160,60,.34)}
/* Which battlefield these stages are fought on: the loaded one lit, the others
   a change of field (a reload). Saying which ground the cards are on is more
   honest than letting a rail of stage cards imply a map each. */
.sb .arenatag .fld{margin:0 calc(var(--u)*9)}
.sb .arenatag .fld[data-field]{cursor:pointer; pointer-events:auto; opacity:.5;
  border-bottom:calc(var(--u)*1) solid rgba(226,186,120,.35)}
.sb .arenatag .fld[data-field]:hover{opacity:1}
.sb .arenatag{margin-top:calc(var(--u)*9); font-size:calc(var(--u)*9); font-weight:700;
  letter-spacing:calc(var(--u)*3); text-transform:uppercase; color:rgba(226,186,120,.58)}
.sb .arenatag b{margin-left:calc(var(--u)*8); padding:calc(var(--u)*2) calc(var(--u)*6);
  font-size:calc(var(--u)*8); letter-spacing:calc(var(--u)*2.2); color:#0e0b07;
  background:linear-gradient(180deg,#ffe6ac,#d9a44a)}
.sb .selhead .mrule{margin:calc(var(--u)*11) auto 0}

/* ------------------------------------------------------------------ rail */
.sb .rail{position:absolute; z-index:2; left:calc(var(--u)*34); top:50%; transform:translateY(-50%);
  width:calc(var(--u)*250); display:flex; flex-direction:column; gap:calc(var(--u)*6)}
/* Panels on these screens sit over a live battlefield, so they carry their own
   dark ground. The 5 %-white wash the rest of the HUD uses works because the
   HUD floats over gameplay the player is already reading past; a card the
   player has to READ needs the contrast under it, not around it. */
.sb .card{position:relative; display:flex; align-items:center; gap:calc(var(--u)*10); width:100%;
  pointer-events:auto; cursor:pointer; text-align:left; font-family:inherit; border:0;
  padding:calc(var(--u)*7) calc(var(--u)*10) calc(var(--u)*7) calc(var(--u)*8);
  background:linear-gradient(90deg, rgba(14,11,8,.80), rgba(9,7,6,.60));
  box-shadow:inset 0 0 0 calc(var(--u)*1) rgba(228,190,116,.14);
  clip-path:polygon(0 0, 100% 0, 100% 100%, calc(var(--u)*9) 100%)}
.sb .card::before{content:""; position:absolute; left:0; top:0; bottom:0; width:calc(var(--u)*2.5);
  background:rgba(228,190,116,.16)}
.sb .card.on{background:linear-gradient(90deg, rgba(58,42,20,.92), rgba(20,15,10,.72))}
.sb .card.on::before{background:linear-gradient(180deg,#ffe6ac,#d9a44a);
  box-shadow:0 0 calc(var(--u)*12) rgba(255,190,90,.75)}
.sb .card canvas{display:block; width:calc(var(--u)*44); height:calc(var(--u)*44); flex:0 0 auto}
.sb .card .cn{min-width:0; flex:1 1 auto}
.sb .card .cn b{display:block; font-size:calc(var(--u)*15); font-weight:400; letter-spacing:calc(var(--u)*1.6);
  color:rgba(238,224,198,.76); white-space:nowrap; overflow:hidden; text-overflow:ellipsis}
.sb .card.on .cn b{color:#fff4dc}
.sb .card .cn span{display:block; margin-top:calc(var(--u)*2); font-size:calc(var(--u)*8.5); font-weight:700;
  letter-spacing:calc(var(--u)*2.4); text-transform:uppercase; color:rgba(224,186,120,.52)}
.sb .card.on .cn span{color:rgba(255,214,142,.86)}
.sb .pips{display:flex; gap:calc(var(--u)*2.5)}
.sb .pips u{display:block; width:calc(var(--u)*7); height:calc(var(--u)*7); text-decoration:none;
  transform:rotate(45deg); background:rgba(228,190,116,.18);
  box-shadow:inset 0 0 0 calc(var(--u)*1) rgba(228,190,116,.34)}
.sb .pips u.f{background:linear-gradient(135deg,#ffe6ac,#d9a44a); box-shadow:0 0 calc(var(--u)*7) rgba(255,190,90,.5)}
.sb .card .pips{margin-top:calc(var(--u)*3)}
.sb .pips.inline{display:inline-flex; vertical-align:baseline;
  transform:translateY(calc(var(--u)*1)); margin-left:calc(var(--u)*4)}

/* -------------------------------------------------------------- dossier */
.sb .dossier{position:absolute; z-index:2; right:calc(var(--u)*34); top:50%; transform:translateY(-50%);
  width:calc(var(--u)*396); text-align:left}
.sb .dossier .dk{font-size:calc(var(--u)*8.5); font-weight:700; letter-spacing:calc(var(--u)*3.6);
  text-transform:uppercase; color:rgba(226,186,120,.64)}
.sb .dossier .dn{margin-top:calc(var(--u)*4); font-size:calc(var(--u)*40); letter-spacing:calc(var(--u)*5);
  line-height:1; color:#fdf3da; text-shadow:0 calc(var(--u)*3) calc(var(--u)*18) rgba(0,0,0,.9)}
.sb .dossier .dt{margin-top:calc(var(--u)*6); font-size:calc(var(--u)*10); font-weight:700;
  letter-spacing:calc(var(--u)*4.4); text-transform:uppercase; color:rgba(232,206,158,.74)}
.sb .dossier .mrule{width:100%; margin:calc(var(--u)*13) 0}
.sb .dossier .dnote{font-size:calc(var(--u)*11); line-height:1.62; color:rgba(232,222,202,.72)}
.sb .dossier .dnote em{font-style:italic; color:rgba(255,224,166,.82)}

.sb .bars{margin-top:calc(var(--u)*4)}
.sb .brow{display:flex; align-items:center; gap:calc(var(--u)*10); padding:calc(var(--u)*4.5) 0}
.sb .brow>.bk{flex:0 0 calc(var(--u)*66); font-size:calc(var(--u)*9); font-weight:700;
  letter-spacing:calc(var(--u)*2.6); text-transform:uppercase; color:rgba(230,202,150,.66)}
.sb .brow>.bt{position:relative; flex:1 1 auto; height:calc(var(--u)*9); background:rgba(0,0,0,.58);
  box-shadow:inset 0 0 0 calc(var(--u)*1) rgba(228,190,116,.20)}
/* Ten notches, so a bar is read as "7 of 10" and not as a mood. */
.sb .brow>.bt>.tick{position:absolute; inset:0; pointer-events:none;
  background:repeating-linear-gradient(90deg, rgba(0,0,0,0) 0 9%, rgba(6,5,4,.85) 9% 10%)}
.sb .brow>.bt>.bf{position:absolute; left:0; top:0; bottom:0; width:0;
  background:linear-gradient(90deg,#9d7128,#ffe2a2)}
.sb .brow>.bn{flex:0 0 calc(var(--u)*34); text-align:right; font-size:calc(var(--u)*11); font-weight:700;
  color:rgba(244,232,208,.86); font-variant-numeric:tabular-nums}
/* The stage dossier reuses the .brow grid but carries prose in the value
   column rather than a bar, so the fact rows get a ruled baseline instead. */
.sb .brow.fact{padding:calc(var(--u)*6) 0; border-bottom:calc(var(--u)*1) solid rgba(228,190,116,.12)}
.sb .brow.fact .fv{flex:1 1 auto; display:flex; align-items:center; gap:calc(var(--u)*6);
  font-size:calc(var(--u)*11); color:rgba(238,228,208,.82); letter-spacing:calc(var(--u)*.6)}
.sb .brow.fact .fv b{font-weight:700; font-size:calc(var(--u)*9); letter-spacing:calc(var(--u)*2.2);
  text-transform:uppercase; color:rgba(226,186,120,.62)}

.sb .dweap{margin-top:calc(var(--u)*12); display:flex; align-items:baseline; gap:calc(var(--u)*10)}
.sb .dweap b{font-size:calc(var(--u)*13); font-weight:700; letter-spacing:calc(var(--u)*3.4);
  text-transform:uppercase; color:#ffdfa4}
.sb .dweap span{font-size:calc(var(--u)*9); font-weight:700; letter-spacing:calc(var(--u)*2.4);
  text-transform:uppercase; color:rgba(224,186,120,.5)}
.sb .dsig{margin-top:calc(var(--u)*11); display:flex; flex-direction:column; gap:calc(var(--u)*4)}
/* Two columns, not an inline-block label: the chain runs to six move names and
   wraps, and a wrapped second line must not slide back under the label. */
.sb .dsig div{display:flex; gap:calc(var(--u)*10); font-size:calc(var(--u)*9.5);
  letter-spacing:calc(var(--u)*1.2); color:rgba(236,224,200,.66); line-height:1.45}
.sb .dsig em{flex:0 0 calc(var(--u)*64); font-style:normal; font-weight:700;
  font-size:calc(var(--u)*8.5); letter-spacing:calc(var(--u)*2.6); text-transform:uppercase;
  color:rgba(224,186,120,.56); padding-top:calc(var(--u)*1)}
.sb .dsig span{flex:1 1 auto; min-width:0}

/* ------------------------------------------------------------ stage map */
/* Centred on the FRAME, not offset by a measured number: the rail and the
   dossier are pinned to the two edges, so any fixed x for the plate only
   lands centred at exactly one aspect ratio and drifts at every other. */
.sb .stagemap{position:absolute; z-index:2; left:50%; top:50%; transform:translate(-50%,-50%);
  width:calc(var(--u)*408); text-align:center}
.sb .stagemap canvas{display:block; width:calc(var(--u)*408); height:calc(var(--u)*408); margin:0 auto;
  box-shadow:0 0 0 calc(var(--u)*1) rgba(228,190,116,.30), 0 calc(var(--u)*10) calc(var(--u)*36) rgba(0,0,0,.7)}
.sb .stagemap .maplegend{margin-top:calc(var(--u)*10); display:flex; justify-content:center;
  gap:calc(var(--u)*14); font-size:calc(var(--u)*8.5); font-weight:700;
  letter-spacing:calc(var(--u)*2); text-transform:uppercase; color:rgba(232,214,182,.62)}
.sb .stagemap .maplegend span{display:flex; align-items:center; gap:calc(var(--u)*5)}
.sb .stagemap .maplegend u{display:block; width:calc(var(--u)*8); height:calc(var(--u)*8);
  border-radius:50%; text-decoration:none}
.sb .stagemap .maplegend u.sui{background:rgba(${rgbOf(uiColor(SIDE.PLAYER))},.34); box-shadow:0 0 0 calc(var(--u)*1) rgba(${rgbOf(uiColor(SIDE.PLAYER))},.95)}
.sb .stagemap .maplegend u.kai{background:rgba(${rgbOf(uiColor(SIDE.ENEMY))},.34); box-shadow:0 0 0 calc(var(--u)*1) rgba(${rgbOf(uiColor(SIDE.ENEMY))},.95)}
.sb .stagemap .maplegend u.free{background:rgba(${rgbOf(uiColor(SIDE.NEUTRAL))},.30); box-shadow:0 0 0 calc(var(--u)*1) rgba(${rgbOf(uiColor(SIDE.NEUTRAL))},.9)}
.sb .stagemap .maplegend u.land{background:none; box-shadow:0 0 0 calc(var(--u)*1.5) rgba(255,232,176,.95)}
.sb .stagemap .mcapn{margin-top:calc(var(--u)*9); font-size:calc(var(--u)*8.5); font-weight:700;
  letter-spacing:calc(var(--u)*2.6); text-transform:uppercase; color:rgba(224,186,120,.5);
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis}

/* --------------------------------------------------------------- muster */
.sb .brief .bwrap{position:relative; z-index:2; margin:auto; width:calc(var(--u)*880); max-width:calc(100% - var(--u)*48);
  display:flex; flex-direction:column; align-items:center}
.sb .brief .duo{display:flex; gap:calc(var(--u)*16); width:100%; margin-top:calc(var(--u)*6)}
.sb .brief .half{flex:1 1 0; min-width:0; text-align:left; padding:calc(var(--u)*16) calc(var(--u)*18);
  background:linear-gradient(180deg, rgba(15,12,9,.86), rgba(8,7,5,.80));
  box-shadow:inset 0 0 0 calc(var(--u)*1) rgba(228,190,116,.18)}
.sb .brief .half>.hk{font-size:calc(var(--u)*8.5); font-weight:700; letter-spacing:calc(var(--u)*3.6);
  text-transform:uppercase; color:rgba(226,186,120,.6)}
.sb .brief .half>.hn{margin-top:calc(var(--u)*5); font-size:calc(var(--u)*27); letter-spacing:calc(var(--u)*3.4);
  line-height:1.1; color:#fdf3da}
.sb .brief .half>.hs{margin-top:calc(var(--u)*6); font-size:calc(var(--u)*9.5); font-weight:700;
  letter-spacing:calc(var(--u)*3); text-transform:uppercase; color:rgba(232,206,158,.66)}
.sb .brief .half>.hb{margin-top:calc(var(--u)*12); font-size:calc(var(--u)*10.5); line-height:1.6;
  color:rgba(238,230,212,.84)}
.sb .brief .hrow{display:flex; gap:calc(var(--u)*13); align-items:center; margin-top:calc(var(--u)*10)}
.sb .brief .hrow canvas{display:block; flex:0 0 auto}
.sb .brief .bpf{width:calc(var(--u)*74); height:calc(var(--u)*74)}
.sb .brief .bmap{width:calc(var(--u)*132); height:calc(var(--u)*132);
  box-shadow:0 0 0 calc(var(--u)*1) rgba(228,190,116,.30)}
.sb .brief .minibars{margin-top:calc(var(--u)*12)}
.sb .brief .minibars .brow{padding:calc(var(--u)*3) 0}
.sb .brief .minibars .bk{flex:0 0 calc(var(--u)*58); font-size:calc(var(--u)*8.5)}
.sb .brief .minibars .bt{height:calc(var(--u)*7)}
.sb .brief .minibars .bn{flex:0 0 calc(var(--u)*30); font-size:calc(var(--u)*10)}
.sb .brief .orders{width:100%; margin-top:calc(var(--u)*14); display:flex; gap:calc(var(--u)*10)}
.sb .brief .ord{flex:1 1 0; padding:calc(var(--u)*11) calc(var(--u)*13); text-align:left;
  background:linear-gradient(180deg, rgba(13,11,8,.82), rgba(7,6,5,.76));
  box-shadow:inset 0 0 0 calc(var(--u)*1) rgba(228,190,116,.14)}
.sb .brief .ord b{display:block; font-size:calc(var(--u)*8.5); font-weight:700; letter-spacing:calc(var(--u)*3);
  text-transform:uppercase; color:rgba(226,186,120,.62)}
.sb .brief .ord span{display:block; margin-top:calc(var(--u)*5); font-size:calc(var(--u)*10.5);
  line-height:1.5; color:rgba(238,230,212,.86)}

/* ----------------------------------------------------------------- foot */
.sb .selfoot{position:absolute; z-index:3; left:0; right:0; bottom:calc(var(--u)*24); text-align:center}
.sb .selfoot .menu{flex-direction:row; gap:calc(var(--u)*10); margin-top:0; justify-content:center}
.sb .selfoot .btn{min-width:calc(var(--u)*206)}
/* A secondary button: same shape, half the voice. Lives here rather than in
   style.js because the select foot is the first screen that needed one, and
   the results card now uses it too. */
.sb .btn.ghost{color:rgba(226,208,176,.5); background:rgba(255,240,210,.015)}
.sb .btn.ghost.on,.sb .btn.ghost:hover{color:#f6ecd6; background:rgba(255,214,140,.07)}
.sb .selfoot .btn.ghost{min-width:calc(var(--u)*130)}
.sb .selfoot .selkeys{margin-top:calc(var(--u)*11); font-size:calc(var(--u)*9); font-weight:700;
  letter-spacing:calc(var(--u)*2.6); text-transform:uppercase; color:rgba(226,206,168,.36)}
.sb .selfoot .selkeys em{font-style:normal; color:rgba(255,226,166,.68)}
.sb .brief .selfoot{position:static; margin-top:calc(var(--u)*18)}

/* ----------------------------------------------------- front-end chrome */
/* Every island the HUD paints for a battle in progress — the health plate, the
   K.O. counter, the hit chain, the officer bar, the minimap, the mission log —
   is noise on a title card or a character select, and worse than noise on the
   two of them that are TRYING to show you a portrait and a map. The ui-front
   class is set by hud.js for every screen outside the battle except pause,
   which keeps
   its HUD precisely because the battle is still underneath it. */
.sb.ui-front .plate,
.sb.ui-front .ko,
.sb.ui-front .chain,
.sb.ui-front .off,
.sb.ui-front .map,
.sb.ui-front .log,
.sb.ui-front .mcap,
.sb.ui-front .ban,
.sb.ui-front .vig-hurt,
.sb.ui-front .vig-low{display:none}

/* ------------------------------------------------------------ narrow -- */
@media (max-width:1180px){
  .sb .rail{width:calc(var(--u)*206)}
  .sb .dossier{width:calc(var(--u)*330)}
  .sb .stagemap{left:calc(var(--u)*264); width:calc(var(--u)*330)}
  .sb .stagemap canvas{width:calc(var(--u)*330); height:calc(var(--u)*330)}
}
@media (max-width:860px), (max-height:560px){
  .sb .sel .selscrim{background:linear-gradient(180deg, rgba(4,3,3,.93), rgba(4,3,3,.72) 30%,
     rgba(4,3,3,.72) 58%, rgba(4,3,3,.95))}
  .sb .selhead{top:calc(var(--safe-t) + var(--u)*12)}
  .sb .seltitle{font-size:calc(var(--u)*22); letter-spacing:calc(var(--u)*6)}
  .sb .rail{left:calc(var(--safe-l) + var(--u)*10); top:calc(var(--u)*96); transform:none;
    width:calc(var(--u)*168); gap:calc(var(--u)*4)}
  .sb .card canvas{width:calc(var(--u)*34); height:calc(var(--u)*34)}
  .sb .card .cn b{font-size:calc(var(--u)*12.5)}
  .sb .dossier{right:calc(var(--safe-r) + var(--u)*10); top:calc(var(--u)*96); transform:none;
    width:calc(var(--u)*250)}
  .sb .dossier .dn{font-size:calc(var(--u)*26)}
  .sb .dossier .dnote{font-size:calc(var(--u)*9.5); line-height:1.5}
  .sb .dsig{display:none}
  .sb .stagemap{display:none}
  .sb .brief .duo{flex-direction:column}
  .sb .brief .orders{flex-direction:column}
  .sb .selfoot .btn{min-width:calc(var(--u)*150)}
}
`;

/* ================================================================ factory */

export function createSelect(host, deps) {
  const { world, sound, onConfirm, onBack } = deps;

  /* --------------------------------------------------------------- DOM -- */

  const el = document.createElement('div');
  el.className = 'selects';

  const stepBar = (i) => STATES.map((s, k) => {
    const label = ['I &middot; Warrior', 'II &middot; Battlefield', 'III &middot; Muster'][k];
    return (k === i ? `<b>${label}</b>` : `<i>${label}</i>`) + (k < 2 ? '<span>/</span>' : '');
  }).join('');

  const railCards = (list, kind) => list.map((r, i) => `
    <button class="card" data-i="${i}" data-kind="${kind}">
      <canvas></canvas>
      <div class="cn">
        <b class="ser">${esc(r.name)}</b>
        <span>${esc(kind === 'hero' ? r.weaponLabel : r.difficulty)}</span>
        ${kind === 'arena' ? pips(r.stars) : ''}
      </div>
    </button>`).join('');

  const barRows = STAT_KEYS.map(k => `
    <div class="brow" data-s="${k}">
      <div class="bk">${STAT_LABEL[k]}</div>
      <div class="bt"><div class="bf"></div><div class="tick"></div></div>
      <div class="bn">0</div>
    </div>`).join('');

  el.innerHTML = `
    <div class="modal sel warsel" data-k="warriors">
      <div class="selscrim"></div>
      <div class="selhead">
        <div class="steps">${stepBar(0)}</div>
        <div class="seltitle ser">CHOOSE YOUR WARRIOR</div>
        <div class="mrule sm"></div>
      </div>
      <div class="rail">${railCards(HEROES, 'hero')}</div>
      <div class="dossier">
        <div class="dk"></div>
        <div class="dn ser"></div>
        <div class="dt"></div>
        <div class="mrule sm"></div>
        <div class="bars">${barRows}</div>
        <div class="dweap"><b></b><span></span></div>
        <div class="mrule sm"></div>
        <div class="dnote"></div>
        <div class="dsig">
          <div><em>Chain</em><span class="sgc"></span></div>
          <div><em>Finisher</em><span class="sgf"></span></div>
          <div><em>Musou</em><span class="sgm"></span></div>
        </div>
      </div>
      <div class="selfoot">
        <div class="menu">
          <button class="btn" data-a="selnext">Choose This Warrior</button>
          <button class="btn ghost" data-a="selback">Back</button>
        </div>
        <div class="selkeys"><em>&uarr; &darr;</em> browse &nbsp;&middot;&nbsp; <em>Enter</em> choose &nbsp;&middot;&nbsp; <em>Esc</em> back</div>
      </div>
    </div>

    <div class="modal sel stagesel" data-k="arena">
      <div class="selscrim"></div>
      <div class="selhead">
        <div class="steps">${stepBar(1)}</div>
        <div class="seltitle ser">CHOOSE THE FIELD</div>
        <div class="arenatag">${ARENAS.map((f) => f === ACTIVE_ARENA
          ? `<span class="fld on">Arena ${f.numeral} &middot; ${esc(f.name)} <b>${esc(f.status)}</b></span>`
          : `<span class="fld" data-field="${f.terrain}">Arena ${f.numeral} &middot; ${esc(f.name)}</span>`).join('')}</div>
        <div class="mrule sm"></div>
      </div>
      <div class="rail">${railCards(STAGES, 'arena')}</div>
      <div class="stagemap">
        <canvas></canvas>
        <div class="maplegend">
          <span><u class="sui"></u>${ALLIES.short}</span>
          <span><u class="kai"></u>${FOES.short}</span>
          <span><u class="free"></u>Contested</span>
          <span><u class="land"></u>You land here</span>
        </div>
        <div class="mcapn">${ACTIVE_ARENA.name} &middot; ${ACTIVE_ARENA.span} m across</div>
      </div>
      <div class="dossier">
        <div class="dk"></div>
        <div class="dn ser"></div>
        <div class="dt"></div>
        <div class="mrule sm"></div>
        <div class="bars stagefacts">
          <div class="brow fact"><div class="bk">Hour</div><div class="fv fhour"></div></div>
          <div class="brow fact"><div class="bk">Weather</div><div class="fv fweather"></div></div>
          <div class="brow fact"><div class="bk">Rating</div><div class="fv fdiff"></div></div>
          <div class="brow fact"><div class="bk">Opposing</div><div class="fv fgen"></div></div>
          <div class="brow fact"><div class="bk">Officers</div><div class="fv foff"></div></div>
        </div>
        <div class="mrule sm"></div>
        <div class="dnote"></div>
      </div>
      <div class="selfoot">
        <div class="menu">
          <button class="btn" data-a="selnext">March On This Field</button>
          <button class="btn ghost" data-a="selback">Back</button>
        </div>
        <div class="selkeys"><em>&uarr; &darr;</em> browse &nbsp;&middot;&nbsp; <em>Enter</em> choose &nbsp;&middot;&nbsp; <em>Esc</em> back</div>
      </div>
    </div>

    <div class="modal sel brief" data-k="briefing">
      <div class="selscrim"></div>
      <div class="bwrap">
        <div class="selhead" style="position:static">
          <div class="steps">${stepBar(2)}</div>
          <div class="seltitle ser">BATTLE ORDERS</div>
          <div class="mrule"></div>
        </div>
        <div class="duo">
          <div class="half">
            <div class="hk">Vanguard</div>
            <div class="hrow">
              <canvas class="bpf"></canvas>
              <div>
                <div class="hn ser bhname"></div>
                <div class="hs bhtitle"></div>
              </div>
            </div>
            <div class="bars minibars">${barRows}</div>
            <div class="hb bhnote"></div>
          </div>
          <div class="half">
            <div class="hk">Field</div>
            <div class="hrow">
              <canvas class="bmap"></canvas>
              <div>
                <div class="hn ser bsname"></div>
                <div class="hs bsmeta"></div>
              </div>
            </div>
            <div class="hb bsbrief"></div>
          </div>
        </div>
        <div class="orders">
          <div class="ord"><b>First</b><span>Break the ${FOES.name} vanguard and hold the ground you land on.</span></div>
          <div class="ord"><b>Then</b><span>Fell their captains, then their sub-general, as each is committed.</span></div>
          <div class="ord"><b>Victory</b><span>${FOE_COMMAND.general}, must fall. Lose your own camp and the field is lost.</span></div>
        </div>
        <div class="selfoot">
          <div class="menu">
            <button class="btn" data-a="selnext">Begin the Battle</button>
            <button class="btn ghost" data-a="selback">Back</button>
          </div>
          <div class="selkeys"><em>Enter</em> to muster &nbsp;&middot;&nbsp; <em>Esc</em> back</div>
        </div>
      </div>
    </div>`;
  host.appendChild(el);

  /* ------------------------------------------------------------- handles */

  const modals = {};
  el.querySelectorAll('.modal').forEach(m => { modals[m.dataset.k] = m; });

  const W = {
    root: modals.warriors,
    cards: [...modals.warriors.querySelectorAll('.card')],
    dk: modals.warriors.querySelector('.dk'),
    dn: modals.warriors.querySelector('.dn'),
    dt: modals.warriors.querySelector('.dt'),
    note: modals.warriors.querySelector('.dnote'),
    weapon: modals.warriors.querySelector('.dweap b'),
    weaponK: modals.warriors.querySelector('.dweap span'),
    sgc: modals.warriors.querySelector('.sgc'),
    sgf: modals.warriors.querySelector('.sgf'),
    sgm: modals.warriors.querySelector('.sgm'),
    bars: {},
  };
  modals.warriors.querySelectorAll('.brow').forEach(r => {
    W.bars[r.dataset.s] = { fill: r.querySelector('.bf'), num: r.querySelector('.bn') };
  });

  const A = {
    root: modals.arena,
    cards: [...modals.arena.querySelectorAll('.card')],
    map: modals.arena.querySelector('.stagemap canvas'),
    caption: modals.arena.querySelector('.mcapn'),
    dk: modals.arena.querySelector('.dk'),
    dn: modals.arena.querySelector('.dn'),
    dt: modals.arena.querySelector('.dt'),
    note: modals.arena.querySelector('.dnote'),
    hour: modals.arena.querySelector('.fhour'),
    weather: modals.arena.querySelector('.fweather'),
    diff: modals.arena.querySelector('.fdiff'),
    gen: modals.arena.querySelector('.fgen'),
    off: modals.arena.querySelector('.foff'),
  };

  const B = {
    root: modals.briefing,
    pf: modals.briefing.querySelector('.bpf'),
    map: modals.briefing.querySelector('.bmap'),
    bars: {},
    hname: modals.briefing.querySelector('.bhname'),
    htitle: modals.briefing.querySelector('.bhtitle'),
    hnote: modals.briefing.querySelector('.bhnote'),
    sname: modals.briefing.querySelector('.bsname'),
    smeta: modals.briefing.querySelector('.bsmeta'),
    sbrief: modals.briefing.querySelector('.bsbrief'),
  };
  modals.briefing.querySelectorAll('.minibars .brow').forEach(r => {
    B.bars[r.dataset.s] = { fill: r.querySelector('.bf'), num: r.querySelector('.bn') };
  });

  /* --------------------------------------------------------------- state */

  let hi = 0;          // highlighted warrior
  let ai = 0;          // highlighted battlefield
  const choice = { heroId: HEROES[0].id, arenaId: STAGES[0].id };

  const hero = () => HEROES[hi];
  const arena = () => STAGES[ai];

  /* ------------------------------------------------------------ painting */

  const dpr = () => Math.min(2, window.devicePixelRatio || 1);
  const unit = () => parseFloat(getComputedStyle(host).getPropertyValue('--u')) || 1;

  /**
   * The size the SHEET gives a canvas, read back off it. `fit()` sets only the
   * backing store — the CSS owns the display box — so painting at a size the
   * JS guessed would silently rescale every stroke the moment a media query
   * changed the box. Computed style resolves even while the modal is closed,
   * which getBoundingClientRect() does not.
   */
  function cssSize(node, fallback) {
    if (!node) return { w: fallback, h: fallback };
    const cs = getComputedStyle(node);
    const w = parseFloat(cs.width), h = parseFloat(cs.height);
    return (w > 0 && h > 0) ? { w, h } : { w: fallback, h: fallback };
  }

  function paintCardPortraits() {
    const d = dpr(), fb = 44 * unit();
    for (let i = 0; i < W.cards.length; i++) {
      const cv = W.cards[i].querySelector('canvas');
      const { w, h } = cssSize(cv, fb);
      portrait(fit(cv, w, h, d), w, h, HEROES[i].art);
    }
  }

  /**
   * A stage thumbnail on each battlefield card: the relief, cropped tight on
   * that stage's own holdings, so the four cards are four different places at
   * a glance rather than four copies of the same square.
   */
  function paintCardMaps() {
    const mm = world?.ui?.minimap;
    const d = dpr(), fb = 44 * unit();
    for (let i = 0; i < A.cards.length; i++) {
      const cv = A.cards[i].querySelector('canvas');
      const { w: s } = cssSize(cv, fb);
      const g = fit(cv, s, s, d);
      const st = STAGES[i];
      g.save();
      g.fillStyle = '#0a0806';
      g.fillRect(0, 0, s, s);
      if (mm && mm.built && mm.relief) {
        const N = mm.relief.width, half = mm.half, size = mm.size;
        const cx = ((st.spawn.x + half) / size) * N, cy = ((st.spawn.z + half) / size) * N;
        const crop = N * 0.42;
        g.drawImage(mm.relief, cx - crop / 2, cy - crop / 2, crop, crop, 0, 0, s, s);
      }
      // landing mark, so even the thumbnail says where you come in
      g.strokeStyle = 'rgba(255,226,166,.85)';
      g.lineWidth = Math.max(1, s * 0.035);
      g.beginPath(); g.arc(s * 0.5, s * 0.5, s * 0.15, 0, Math.PI * 2); g.stroke();
      g.strokeStyle = 'rgba(228,190,116,.42)';
      g.lineWidth = Math.max(1, s * 0.045);
      g.strokeRect(0, 0, s, s);
      g.restore();
    }
  }

  /**
   * The big battlefield plate. Baked relief underneath, then the valley inked
   * on top from the layout tables: the Brantas, the five roads, the seven
   * holdings. The highlighted stage lights its own holdings and drops a
   * landing chevron.
   */
  function drawValley(cv, fallbackPx, detail = true) {
    if (!cv) return;
    const S = cssSize(cv, fallbackPx).w;
    const g = fit(cv, S, S, dpr());
    const mm = world?.ui?.minimap;
    const half = mm && mm.built ? mm.half : 260;
    const size = mm && mm.built ? mm.size : 520;
    const P = (x, z) => [((x + half) / size) * S, ((z + half) / size) * S];

    g.save();
    g.fillStyle = '#0b0907';
    g.fillRect(0, 0, S, S);
    if (mm && mm.built && mm.relief) {
      g.drawImage(mm.relief, 0, 0, S, S);
      // The relief is baked for a 384 px plate in the corner of a lit scene.
      // Blown up to a 408 px card behind a scrim it goes flat — mid-grey
      // hillside, mid-grey road, mid-grey fort — and the ink drawn on top has
      // nothing to sit against. One multiply pass of the image over itself is
      // a gamma of 2: it opens the shadow between the ridges and leaves the
      // ridge tops where they were.
      g.globalCompositeOperation = 'multiply';
      g.globalAlpha = 0.82;
      g.drawImage(mm.relief, 0, 0, S, S);
      g.globalAlpha = 1;
      // ...and a warm additive pass brings the midtones back as parchment
      // rather than as the grey the multiply left behind.
      g.globalCompositeOperation = 'lighter';
      const warm = g.createRadialGradient(S * 0.5, S * 0.44, S * 0.06, S * 0.5, S * 0.5, S * 0.80);
      warm.addColorStop(0, 'rgba(96,74,40,.20)');
      warm.addColorStop(1, 'rgba(32,23,12,.05)');
      g.fillStyle = warm;
      g.fillRect(0, 0, S, S);
      g.globalCompositeOperation = 'source-over';
    }

    // edge falloff, so the plate has a dark rim for its frame to sit on
    const vg = g.createRadialGradient(S * 0.5, S * 0.5, S * 0.30, S * 0.5, S * 0.5, S * 0.74);
    vg.addColorStop(0, 'rgba(6,5,4,0)');
    vg.addColorStop(1, 'rgba(6,5,4,.58)');
    g.fillStyle = vg;
    g.fillRect(0, 0, S, S);

    // survey grid — the single cheapest thing that says "map" and not "photo"
    g.strokeStyle = 'rgba(240,214,160,.11)';
    g.lineWidth = Math.max(1, S * 0.0025);
    for (let i = 1; i < 8; i++) {
      const q = Math.round((i / 8) * S) + 0.5;
      g.beginPath(); g.moveTo(q, 0); g.lineTo(q, S); g.stroke();
      g.beginPath(); g.moveTo(0, q); g.lineTo(S, q); g.stroke();
    }

    // --- the Brantas
    g.lineJoin = 'round'; g.lineCap = 'round';
    g.strokeStyle = 'rgba(112,168,214,.72)';
    g.lineWidth = Math.max(2, S * 0.014);
    g.beginPath();
    for (let i = 0; i < RIVER_PTS.length; i++) {
      const [x, y] = P(RIVER_PTS[i][0], RIVER_PTS[i][1]);
      if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
    }
    g.stroke();

    // --- roads
    g.strokeStyle = 'rgba(244,220,168,.46)';
    g.lineWidth = Math.max(1.2, S * 0.0075);
    g.setLineDash([S * 0.018, S * 0.013]);
    for (const r of ROAD_DEFS) {
      g.beginPath();
      for (let i = 0; i < r.pts.length; i++) {
        const [x, y] = P(r.pts[i][0], r.pts[i][1]);
        if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
      }
      g.stroke();
    }
    g.setLineDash([]);

    // --- the bridge, drawn across the ford (an arena may have none)
    if (HAS_BRIDGE) {
      const [ax, ay] = P(BRIDGE.ax, BRIDGE.az);
      const [bx, by] = P(BRIDGE.bx, BRIDGE.bz);
      g.strokeStyle = 'rgba(244,220,170,.62)';
      g.lineWidth = Math.max(2, S * 0.009);
      g.beginPath(); g.moveTo(ax, ay); g.lineTo(bx, by); g.stroke();
    }

    // --- walls, on a walled arena: brick-red ink, broken where the gates are.
    // They are what the player navigates by on this ground; a map without them
    // shows courts with nothing between them.
    if (WALLS.n) {
      g.strokeStyle = 'rgba(200,112,80,.88)';
      g.lineCap = 'butt';
      for (const r of WALLS.runs) {
        g.lineWidth = Math.max(1.6, (r.t0 / size) * S * 1.5);
        const [ax, ay] = P(r.x0, r.z0), [bx, by] = P(r.x1, r.z1);
        g.beginPath(); g.moveTo(ax, ay); g.lineTo(bx, by); g.stroke();
      }
      g.lineCap = 'round';
    }

    // --- holdings
    const live = new Set(arena().sites || []);
    for (const s of SITES) {
      const [x, y] = P(s.x, s.z);
      const on = live.has(s.id);
      const r = Math.max(3, (s.radius / size) * S) * (on ? 1.15 : 0.8);
      const col = rgbOf(uiColor(s.team));

      if (on) {
        const gl = g.createRadialGradient(x, y, r * 0.2, x, y, r * 2.5);
        gl.addColorStop(0, `rgba(${col},.34)`);
        gl.addColorStop(1, `rgba(${col},0)`);
        g.fillStyle = gl;
        g.beginPath(); g.arc(x, y, r * 2.5, 0, Math.PI * 2); g.fill();
      }
      g.strokeStyle = `rgba(${col},${on ? 0.95 : 0.34})`;
      g.lineWidth = Math.max(1, S * (on ? 0.006 : 0.004));
      g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.stroke();
      g.fillStyle = `rgba(${col},${on ? 0.34 : 0.10})`;
      g.fill();

      if (on && detail) {
        g.font = `700 ${S * 0.0245}px "Liberation Sans",Helvetica,Arial,sans-serif`;
        g.textAlign = 'center';
        g.textBaseline = 'bottom';
        g.fillStyle = 'rgba(6,5,4,.85)';
        g.fillText(s.name.toUpperCase(), x + 1, y - r - S * 0.010 + 1);
        g.fillStyle = 'rgba(255,236,196,.94)';
        g.fillText(s.name.toUpperCase(), x, y - r - S * 0.010);
      }
    }

    // --- where the hero lands
    {
      const [x, y] = P(arena().spawn.x, arena().spawn.z);
      const R = S * 0.026;
      g.strokeStyle = 'rgba(255,232,176,.95)';
      g.lineWidth = Math.max(1.5, S * 0.0055);
      g.beginPath(); g.arc(x, y, R, 0, Math.PI * 2); g.stroke();
      g.beginPath();
      g.moveTo(x, y - R * 2.25); g.lineTo(x - R * 0.72, y - R * 1.15); g.lineTo(x + R * 0.72, y - R * 1.15);
      g.closePath();
      g.fillStyle = 'rgba(255,232,176,.95)';
      g.fill();
      for (const a of [0, Math.PI * 0.5, Math.PI, Math.PI * 1.5]) {
        g.beginPath();
        g.moveTo(x + Math.cos(a) * R * 1.35, y + Math.sin(a) * R * 1.35);
        g.lineTo(x + Math.cos(a) * R * 1.9, y + Math.sin(a) * R * 1.9);
        g.stroke();
      }
    }

    // --- survey frame
    g.strokeStyle = 'rgba(228,190,116,.34)';
    g.lineWidth = Math.max(1, S * 0.004);
    g.strokeRect(S * 0.012, S * 0.012, S * 0.976, S * 0.976);
    if (detail) {
      g.font = `700 ${S * 0.022}px "Liberation Sans",Helvetica,Arial,sans-serif`;
      g.fillStyle = 'rgba(228,196,140,.5)';
      g.textAlign = 'left'; g.textBaseline = 'top';
      g.fillText('N', S * 0.03, S * 0.028);
      g.beginPath();
      g.moveTo(S * 0.038, S * 0.075); g.lineTo(S * 0.038, S * 0.115);
      g.moveTo(S * 0.038, S * 0.070); g.lineTo(S * 0.026, S * 0.090);
      g.moveTo(S * 0.038, S * 0.070); g.lineTo(S * 0.050, S * 0.090);
      g.strokeStyle = 'rgba(228,196,140,.5)';
      g.lineWidth = Math.max(1, S * 0.0035);
      g.stroke();
    }
    g.restore();
  }

  const paintStageMap = () => drawValley(A.map, 408 * unit(), true);
  /* The muster plate gets the same valley at a third the size, with the site
   * names and the compass dropped: at 132 px they are illegible smears, and an
   * unreadable label is worse than no label. */
  const paintBriefMap = () => drawValley(B.map, 132 * unit(), false);

  function paintBriefPortrait() {
    const { w, h } = cssSize(B.pf, 74 * unit());
    portrait(fit(B.pf, w, h, dpr()), w, h, hero().art);
  }

  /* ------------------------------------------------------------- syncing */

  function syncWarrior(preview) {
    const h = hero();
    const st = statsFor(h.moveset);
    const sg = signatureFor(h.moveset);

    for (let i = 0; i < W.cards.length; i++) W.cards[i].classList.toggle('on', i === hi);
    W.dk.textContent = `${h.kingdom} · ${h.epithet}`;
    W.dn.textContent = h.name;
    W.dt.textContent = h.title;
    W.note.textContent = h.note;
    W.weapon.textContent = h.weaponLabel;
    W.weaponK.textContent = 'Weapon';
    W.sgc.textContent = sg.chain.join(' · ');
    W.sgf.textContent = sg.finisher;
    W.sgm.textContent = sg.musou;
    for (const k of STAT_KEYS) {
      const b = W.bars[k];
      if (!b) continue;
      b.fill.style.width = (Math.max(0, Math.min(10, st[k])) * 10).toFixed(1) + '%';
      b.num.textContent = st[k].toFixed(1);
    }
    choice.heroId = h.id;
    if (preview !== false) { previewHero(world, h); faceLens(); }
  }

  function syncArena() {
    const a = arena();
    for (let i = 0; i < A.cards.length; i++) A.cards[i].classList.toggle('on', i === ai);
    A.dk.textContent = a.region;
    A.dn.textContent = a.name;
    A.dt.innerHTML = `${esc(a.difficulty)} ${pips(a.stars, 'inline')}`;
    A.hour.textContent = a.hour;
    A.weather.textContent = a.weather;
    A.diff.innerHTML = `${pips(a.stars, 'inline')} <b>${a.stars} of 5</b>`;
    A.gen.textContent = FOE_COMMAND.general;
    A.off.textContent = FOE_COMMAND.officers;
    A.note.textContent = a.brief;
    A.caption.textContent = `${ACTIVE_ARENA.span} m across · ${a.sites.length} contested holdings`;
    choice.arenaId = a.id;
    paintStageMap();
  }

  function syncBrief() {
    const h = hero(), a = arena();
    B.hname.textContent = h.name;
    B.htitle.textContent = `${h.title} · ${h.weaponLabel}`;
    B.hnote.textContent = h.line;
    B.sname.textContent = a.name;
    B.smeta.innerHTML = `${esc(a.hour)} · ${esc(a.difficulty)} ${pips(a.stars, 'inline')}`;
    B.sbrief.textContent = a.brief;
    const st = statsFor(h.moveset);
    for (const k of STAT_KEYS) {
      const b = B.bars[k];
      if (!b) continue;
      b.fill.style.width = (Math.max(0, Math.min(10, st[k])) * 10).toFixed(1) + '%';
      b.num.textContent = st[k].toFixed(1);
    }
    paintBriefPortrait();
    paintBriefMap();
  }

  /* ---------------------------------------------------------- navigation */

  function moveHi(d) {
    const n = HEROES.length;
    hi = (hi + d % n + n) % n;
    sound('ui_hover');
    syncWarrior();
  }
  function moveAi(d) {
    const n = STAGES.length;
    ai = (ai + d % n + n) % n;
    sound('ui_hover');
    syncArena();
  }

  /**
   * Change of field. An arena is carved at boot from `?arena=`, so going to
   * another one is a reload — and the reload comes back to THIS step with the
   * same warrior in hand (`front=field`, read by hud.js), not to the title.
   */
  function switchField(id) {
    if (!id || id === ARENA_ID || !ARENAS.some((f) => f.terrain === id)) return false;
    const q = new URLSearchParams(location.search);
    q.set('arena', id); q.set('front', 'field'); q.set('hero', choice.heroId);
    sound('ui_confirm');
    location.search = q.toString();
    return true;
  }

  el.addEventListener('pointerdown', (ev) => {
    const f = ev.target.closest ? ev.target.closest('[data-field]') : null;
    if (f) { ev.preventDefault(); switchField(f.dataset.field); return; }
    const c = ev.target.closest ? ev.target.closest('.card') : null;
    if (!c) return;
    ev.preventDefault();
    const i = +c.dataset.i;
    if (c.dataset.kind === 'hero') {
      if (i === hi) { sound('ui_confirm'); onConfirm && onConfirm('warriors'); return; }
      hi = i; sound('ui_click'); syncWarrior();
    } else {
      if (i === ai) { sound('ui_confirm'); onConfirm && onConfirm('arena'); return; }
      ai = i; sound('ui_click'); syncArena();
    }
  });
  el.addEventListener('pointerover', (ev) => {
    const c = ev.target.closest ? ev.target.closest('.card') : null;
    if (!c) return;
    const i = +c.dataset.i;
    if (c.dataset.kind === 'hero') { if (i !== hi) { hi = i; sound('ui_hover'); syncWarrior(); } }
    else if (i !== ai) { ai = i; sound('ui_hover'); syncArena(); }
  });

  /**
   * Arrow / WASD keys drive the ROSTER, not the two buttons in the foot —
   * browsing is what these screens are for, and a player pressing Down expects
   * the next warrior. Enter confirms, Escape backs out (menus.js owns Escape).
   * Returns true when the key was consumed.
   */
  function key(ev, state) {
    const k = ev.key;
    const isW = state === 'warriors', isA = state === 'arena';
    if (!isW && !isA && state !== 'briefing') return false;

    if (k === 'ArrowDown' || k === 's' || k === 'S' || k === 'ArrowRight' || k === 'd' || k === 'D') {
      if (isW) { ev.preventDefault(); moveHi(1); return true; }
      if (isA) { ev.preventDefault(); moveAi(1); return true; }
    }
    if (k === 'ArrowUp' || k === 'w' || k === 'W' || k === 'ArrowLeft' || k === 'a' || k === 'A') {
      if (isW) { ev.preventDefault(); moveHi(-1); return true; }
      if (isA) { ev.preventDefault(); moveAi(-1); return true; }
    }
    if (k === 'Enter' || k === ' ') {
      ev.preventDefault();
      sound('ui_confirm');
      onConfirm && onConfirm(state);
      return true;
    }
    if (k === 'Backspace') {
      ev.preventDefault();
      sound('ui_back');
      onBack && onBack(state);
      return true;
    }
    return false;
  }

  /* ------------------------------------------------------- screen enter */

  /**
   * Warrior select puts the camera on the hero. `setMode('select')` supplies
   * the portrait boom; the turntable comes from the PERSISTENT orbit, because
   * the mode orbit decays by design (see the MODES table in camera.js) and a
   * browse screen has to hold one speed for as long as the player takes.
   */
  /**
   * The field is already live behind these screens — the director fills it at
   * boot and the Kidul will happily put a spear through a man standing still on
   * his own character-select turntable. `godMode` makes the hero unhittable
   * for as long as a selection screen is open (`ai.js` checks the flag before
   * it ever calls `combat.damage` on the player), so browsing the roster
   * cannot leave you at half health, mid-flinch, at the moment the battle
   * starts. The previous value is restored on the way out; `mission.reset()`
   * owns the flag from there.
   */
  let godHeld = false, godWas = false;
  function holdGod(on) {
    const f = world?.flags;
    if (!f) return;
    if (on && !godHeld) { godHeld = true; godWas = !!f.godMode; f.godMode = true; }
    else if (!on && godHeld) { godHeld = false; f.godMode = godWas; }
  }

  /**
   * Turn the hero to face the lens.
   *
   * The follow rig lives BEHIND its subject by construction, so a select
   * screen that merely points it at the hero photographs the back of his
   * cape — which is exactly what the first capture of this screen showed:
   * three warriors, three identical blue backs, and not one of the three
   * weapons the cards were describing anywhere in frame. The camera sits at
   * `focus - (sin yaw, cos yaw) * dist`, and an entity's facing is
   * `(sin yaw, cos yaw)` in the same convention, so the man faces the lens at
   * `camYaw + PI`. Set once per screen, not per frame: the boom keeps orbiting
   * afterwards, which is what makes it a turntable rather than a mugshot.
   */
  function faceLens() {
    const w = world, c = w?.camrig, e = w?.entities, id = w?.playerId;
    if (!c || !e || !(id >= 0)) return;
    const y = (c.solved?.yaw ?? c.yaw ?? 0) + Math.PI;
    const wrapped = Math.atan2(Math.sin(y), Math.cos(y));
    e.yaw[id] = wrapped;
    e.yawGoal[id] = wrapped;
  }

  /**
   * Clear the field for the portrait. The valley is populated from boot, and
   * at a 4.4 m boom a rank of Kidul spearmen stands between the lens and the
   * subject — the first capture had four of them and a banner across the
   * hero's chest. The crowd renderer draws every man but the hero, so one
   * switch empties the stage and leaves the terrain, the sky, the forts and
   * the banners as the backdrop they should have been all along.
   */
  function clearStage(on) {
    try { world?.crowd?.setVisible?.(!on); } catch (e) { /* crowd not built */ }
  }

  function enter(state) {
    if (!STATES.includes(state)) return;
    holdGod(true);
    clearStage(true);
    const c = world?.camrig;
    if (c) c.setMode('select');
    if (state === 'warriors') {
      paintCardPortraits();
      syncWarrior();
      // 8 deg/s: fast enough that the man is visibly turning, slow enough that
      // he is still near frontal after the several seconds a player spends
      // reading five stat bars and a move list.
      if (c) c.orbit(0.14);
    } else if (state === 'arena') {
      paintCardMaps();
      syncArena();
      if (c) c.orbit(0.08);
    } else if (state === 'briefing') {
      syncBrief();
      if (c) c.orbit(0.05);
    }
    faceLens();
  }

  function leave(state, next) {
    if (!STATES.includes(state)) return;
    if (STATES.includes(next)) return;      // moving between the three: hold the pose
    holdGod(false);
    clearStage(false);
    const c = world?.camrig;
    if (c) { c.orbit(0); c.setMode('follow'); }
  }

  function resize() {
    paintCardPortraits();
    paintCardMaps();
    paintStageMap();
    paintBriefPortrait();
    paintBriefMap();
  }

  // First paint. The relief may not be baked yet at construction time, so the
  // stage plate is repainted on enter() as well.
  syncWarrior(false);
  syncArena();

  return {
    el, modals, enter, leave, key, resize,
    choice,
    hero, arena,
    /* The roster itself, so a headless driver can ask what is on offer instead
     * of hard-coding a count. The Step 8 contract check reads this to prove
     * every moveset has a hero who carries it — a weapon nobody can select is
     * a weapon that ships complete, tested and unplayable. */
    heroes: HEROES, stages: STAGES,
    /** Every battlefield, and a way onto another one (reloads the page). */
    fields: ARENAS, field(id) { return switchField(id); },
    get heroIndex() { return hi; },
    get arenaIndex() { return ai; },
    setHero(i) { hi = Math.max(0, Math.min(HEROES.length - 1, i | 0)); syncWarrior(); return hi; },
    setArena(i) { ai = Math.max(0, Math.min(STAGES.length - 1, i | 0)); syncArena(); return ai; },
    states: STATES,
  };
}
